/**
 * NavMenuButton.tsx — Inline hamburger button block.
 *
 * Placed inside a header layout row (Single Row Header preset). On click,
 * dispatches `agentic:toggle-side-nav` on `window`; the global Header.tsx
 * listens for this event and opens the left-slide nav drawer that's
 * otherwise reserved for mobile.
 *
 * No state of its own — the drawer state lives in Header.tsx so the
 * single drawer instance services both the mobile-chrome hamburger and
 * any inline hamburger blocks the editor places into a header preset.
 */
import React, { useEffect, useState } from 'react';
import type { BlockComponentProps } from '../../renderer/BlockRegistry';

interface NavMenuButtonSettings { iconSize?: number; pillPadding?: string }
interface NavMenuButtonContent { ariaLabel?: string; variant?: string; label?: string }

export const NavMenuButton: React.FC<BlockComponentProps> = ({ data }) => {
    const settings: NavMenuButtonSettings = (data?.settings as NavMenuButtonSettings | undefined) ?? {};
    const content: NavMenuButtonContent = (data?.content as NavMenuButtonContent | undefined) ?? {};
    // iconSize: when admin explicitly passes a number, honour it. Otherwise
    // flow from --style-header-icon-size so the active style preset controls
    // every header icon at the same dial.
    const iconSizeNumeric: number | undefined = settings.iconSize;
    const iconSizeCss: string = iconSizeNumeric != null
        ? `${iconSizeNumeric}px`
        : 'var(--style-header-icon-size, 22px)';
    const padding: string = settings.pillPadding ?? 'var(--style-header-icon-pill-padding, 10px 14px)';
    const ariaLabel: string = content.ariaLabel ?? 'Open menu';

    // Variant: icon-hamburger (default) | icon-text | animated-burger
    const variantRaw = content.variant ?? ((data as { variant?: string } | undefined)?.variant) ?? 'icon-hamburger';
    const variant: 'icon-hamburger' | 'icon-text' | 'animated-burger' =
        ['icon-hamburger','icon-text','animated-burger'].includes(variantRaw)
            ? variantRaw as 'icon-hamburger' | 'icon-text' | 'animated-burger'
            : 'icon-hamburger';
    const label: string = content.label ?? 'MENU';

    // Track open state for the animated-burger morph. Listens to the same
    // global toggle event the drawer uses so the icon stays in sync.
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onToggle = () => setOpen(v => !v);
        const onClose = () => setOpen(false);
        window.addEventListener('agentic:toggle-side-nav', onToggle);
        window.addEventListener('agentic:close-side-nav', onClose);
        return () => {
            window.removeEventListener('agentic:toggle-side-nav', onToggle);
            window.removeEventListener('agentic:close-side-nav', onClose);
        };
    }, []);

    const onClick = () => {
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent('agentic:toggle-side-nav'));
    };

    const buttonBaseStyle: React.CSSProperties = {
        padding,
        // Hamburger sits in mobile header chrome — chrome icon colour first.
        color: 'var(--color-mobile-nav-icon, var(--color-mobile-header-text, var(--color-header-icon, var(--text-primary, currentColor))))',
        background: 'transparent',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--style-header-icon-pill-radius, var(--style-pill-radius, 999px))',
        lineHeight: 1,
        cursor: 'pointer',
    };

    // Variant: icon-hamburger — plain three-line icon. Default.
    if (variant === 'icon-hamburger') {
        return (
            <button
                type="button"
                onClick={onClick}
                aria-label={ariaLabel}
                aria-expanded={open}
                className="inline-flex items-center justify-center transition-colors duration-200"
                style={buttonBaseStyle}
            >
                <svg
                    style={{ width: iconSizeCss, height: iconSizeCss }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>
        );
    }

    // Variant: icon-text — hamburger + uppercase MENU label.
    if (variant === 'icon-text') {
        return (
            <button
                type="button"
                onClick={onClick}
                aria-label={ariaLabel}
                aria-expanded={open}
                className="inline-flex items-center justify-center gap-2 transition-colors duration-200"
                style={{
                    ...buttonBaseStyle,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                }}
            >
                <svg
                    style={{
                        // Slightly smaller than the header icon size to balance the MENU label
                        width: `calc(${iconSizeCss} - 2px)`,
                        height: `calc(${iconSizeCss} - 2px)`,
                    }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span>{label}</span>
            </button>
        );
    }

    // Variant: animated-burger — three lines morph into X on open. Pure CSS
    // transforms on the three <span> bars; respects prefers-reduced-motion.
    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                .agentic-burger {
                    width: 24px;
                    height: 18px;
                    position: relative;
                    display: inline-block;
                }
                .agentic-burger > span {
                    position: absolute;
                    left: 0;
                    width: 100%;
                    height: 2px;
                    background: currentColor;
                    border-radius: 2px;
                    transition: transform 280ms cubic-bezier(.4,0,.2,1), opacity 200ms ease, top 280ms cubic-bezier(.4,0,.2,1);
                }
                .agentic-burger > span:nth-child(1) { top: 0; }
                .agentic-burger > span:nth-child(2) { top: 50%; transform: translateY(-50%); }
                .agentic-burger > span:nth-child(3) { top: 100%; transform: translateY(-100%); }
                .agentic-burger.is-open > span:nth-child(1) { top: 50%; transform: translateY(-50%) rotate(45deg); }
                .agentic-burger.is-open > span:nth-child(2) { opacity: 0; }
                .agentic-burger.is-open > span:nth-child(3) { top: 50%; transform: translateY(-50%) rotate(-45deg); }
                @media (prefers-reduced-motion: reduce) {
                    .agentic-burger > span { transition: none; }
                }
            ` }} />
            <button
                type="button"
                onClick={onClick}
                aria-label={open ? 'Close menu' : ariaLabel}
                aria-expanded={open}
                className="inline-flex items-center justify-center transition-colors duration-200"
                style={buttonBaseStyle}
            >
                <span className={`agentic-burger ${open ? 'is-open' : ''}`} aria-hidden="true">
                    <span /><span /><span />
                </span>
            </button>
        </>
    );
};

export default NavMenuButton;
